import type { ComponentProps, ComponentType } from 'react';
import { newListingSourceAtom } from 'app/atoms';
import { ErrorBoundary } from 'app/components/error-boundary';
import { Column, Row } from 'app/design/primitives/box';
import { FormError } from 'app/design/primitives/form-error';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { Accessory } from 'app/design/primitives/page/accessory';
import { Switch } from 'app/design/primitives/switch';
import { Text } from 'app/design/primitives/text';
import { useForm, useFormContext } from 'app/hooks/form';
import { useNavigationOptions } from 'app/hooks/navigation';
import { Event, track } from 'app/lib/analytics';
import { StorageKey, useStoredValue } from 'app/lib/storage';
import { useAllAddresses } from 'app/network/address';
import { useListingVisibilityGroups } from 'app/network/groups';
import { useMyActivePlans } from 'app/network/plan';
import { useMyProfile } from 'app/network/profile';
import { BaconSection } from 'app/screens/new/components/bacon-section';
import { AvailabilitySection } from 'app/screens/shared/one-listing/components/availability-section';
import type { CreateListingDTO, NewListingSource, Listing } from 'app/types/listing';
import type { NavigationScreen, SpecificNavigationProp, SpecificRouteProp } from 'app/types/navigation';
import type { ImagePickerAsset } from 'expo-image-picker';
import { useAtomValue } from 'jotai/index';
import { useResetAtom } from 'jotai/utils';
import React, { useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import Animated, {
  Easing,
  FadeInUp,
  FadeOutUp,
  LinearTransition,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated';
import * as yup from 'yup';
import TravelDistanceSection from '../shared/travel-distance';
import { Actions } from './components/actions';
import { DateSection } from './components/date-section';
import { GeneralSection } from './components/general-section';
import { GroupsSection } from './components/groups-section';
import { ItemInformationSection } from './components/item-information-section';

import { IconClock, IconX } from '@tabler/icons-react-native';
import { MagicEdit } from 'app/design/icons/magic-edit';
import { AnimatedSignal, Signal } from 'app/design/icons/signal';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Button, PressableType } from 'app/design/primitives/button';
import { Modal } from 'app/design/primitives/modal';
import { isDonationListing, isListingAvailabilityRangeValid } from 'app/lib/listing';
import { useAnalyzeListingImage } from 'app/network/listing';
import { MAX_RADIUS_IN_METERS } from 'app/network/preferences';
import { useUser } from 'app/network/user';
import { createTheme as createDSTheme } from 'app/shopify';
import { ListingSubtype, ListingType, TransactionMode } from 'app/types/listing';
import { WhiteLabel } from 'app/types/white-labels';
import { ICON_STROKE_WIDTH, MAX_LISTING_DUPLICATES } from 'app/utils/constants';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import { usePageTitle } from 'app/utils/web-helpers';
import { AxiosError } from 'axios';
import type { NavigationAction } from '@react-navigation/native';
import { Trans, useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { Pressable } from 'react-native-gesture-handler';
import Toast from 'react-native-toast-message';
import { BonificationBadge } from '../billboard/all-listings/components/bonification-banner';
import { MultiLineTextInput } from './components/automatic-message-input';
import { HauteGaspesieOfferForm } from './components/haute-gaspesie-offer-form';
import { PublicServiceRequestSection } from './components/public-service-request-section';
import { TransactionModeSection } from './components/transaction-mode-section';
import { useAutomaticMessage } from './use-automatic-message';
const theme = createDSTheme();

type OfferCreationStep = 'image' | 'generate' | 'details' | 'image-and-details';

function arePicturesEquivalent(left?: ImagePickerAsset | null, right?: ImagePickerAsset | null) {
  if (!left || !right) return false;
  if (left === right) return true;
  if (left.assetId && right.assetId) return left.assetId === right.assetId;
  return left.uri === right.uri;
}

function getInitialOfferCreationStep(defaultValues?: NewScreenParams['defaultValues']): OfferCreationStep {
  if (defaultValues?.type && defaultValues.type !== ListingType.offer) {
    return 'details';
  }

  const hasPrefilledDetails = Boolean(
    defaultValues?.description ||
    defaultValues?.item?.name ||
    defaultValues?.categoryPrimaryId ||
    defaultValues?.estimatedPrice ||
    defaultValues?.price ||
    defaultValues?.inventory ||
    defaultValues?.quantity ||
    defaultValues?.loanFee ||
    defaultValues?.loanFeeDescription ||
    defaultValues?.loanFeePaymentMethod ||
    defaultValues?.serviceCategoryId ||
    defaultValues?.pictureUrl ||
    defaultValues?.groupIds?.length ||
    defaultValues?.addressIds?.length ||
    defaultValues?.transactionMode,
  );
  const hasPicture = Boolean(defaultValues?.pictureUrl);
  return hasPrefilledDetails && hasPicture ? 'image-and-details' : hasPrefilledDetails ? 'details' : 'image';
}

function ShimmerLine({
  width,
  delay,
  shimmerX,
}: {
  width: `${number}%`;
  delay: number;
  shimmerX: SharedValue<number>;
}) {
  const style = useAnimatedStyle(() => ({
    opacity: 0.4 + 0.6 * Math.max(0, 1 - Math.abs(shimmerX.value - delay / 600) * 1.5),
  }));
  return (
    <Animated.View
      style={[{ height: 10, borderRadius: 5, backgroundColor: theme.colors['surface.brand.default'], width }, style]}
    />
  );
}

function AiAnalyzingBanner({ t }: { t: ReturnType<typeof useTranslation<'new'>>['t'] }) {
  const shimmerX = useSharedValue(0);
  const dotOpacity1 = useSharedValue(0.3);
  const dotOpacity2 = useSharedValue(0.3);
  const dotOpacity3 = useSharedValue(0.3);

  useEffect(() => {
    shimmerX.value = withRepeat(withTiming(1, { duration: 1800, easing: Easing.inOut(Easing.ease) }), -1, true);
    const dotPulse = (delay: number) =>
      withDelay(
        delay,
        withRepeat(
          withSequence(
            withTiming(1, { duration: 500, easing: Easing.inOut(Easing.sin) }),
            withTiming(0.3, { duration: 500, easing: Easing.inOut(Easing.sin) }),
          ),
          -1,
          true,
        ),
      );
    dotOpacity1.value = dotPulse(0);
    dotOpacity2.value = dotPulse(200);
    dotOpacity3.value = dotPulse(400);
  }, [shimmerX, dotOpacity1, dotOpacity2, dotOpacity3]);

  const dot1Style = useAnimatedStyle(() => ({ opacity: dotOpacity1.value }));
  const dot2Style = useAnimatedStyle(() => ({ opacity: dotOpacity2.value }));
  const dot3Style = useAnimatedStyle(() => ({ opacity: dotOpacity3.value }));

  return (
    <Column
      style={{
        borderRadius: theme.borderRadii['radius.lg'],
        backgroundColor: theme.colors['surface.tertiary'],
        borderWidth: 1,
        borderColor: theme.colors['surface.tertiary'],
        padding: theme.spacing['spacing.general.x4'],
        gap: theme.spacing['spacing.general.x4'],
      }}
    >
      <Row alignItems="center" gap="spacing.general.x2">
        <Column
          alignItems="center"
          justifyContent="center"
          style={{
            backgroundColor: theme.colors['surface.tertiary'],
            borderRadius: theme.borderRadii['radius.full'],
            width: 32,
            height: 32,
          }}
        >
          <AnimatedSignal height={20} width={20} color={theme.colors['text.brand']} />
        </Column>
        <Row alignItems="center" style={{ flex: 1 }}>
          <Text variant="bodyBase" fontWeight="700" fontSize={14} style={{ color: theme.colors['text.brand'] }}>
            {t('itemInformationSection.aiGenerate.loading')}
          </Text>
          <Animated.Text style={[aiStyles.dot, dot1Style]}>.</Animated.Text>
          <Animated.Text style={[aiStyles.dot, dot2Style]}>.</Animated.Text>
          <Animated.Text style={[aiStyles.dot, dot3Style]}>.</Animated.Text>
        </Row>
      </Row>
      <Column gap="spacing.general.x1">
        <ShimmerLine width="55%" delay={0} shimmerX={shimmerX} />
        <ShimmerLine width="100%" delay={200} shimmerX={shimmerX} />
        <ShimmerLine width="80%" delay={400} shimmerX={shimmerX} />
      </Column>
    </Column>
  );
}

function AiGeneratePromptBanner({
  onPress,
  onSkip,
  t,
}: {
  onPress: () => void;
  onSkip: () => void;
  t: ReturnType<typeof useTranslation<'new'>>['t'];
}) {
  return (
    <Column
      style={{
        borderRadius: theme.borderRadii['radius.lg'],
        backgroundColor: theme.colors['surface.primary-neutral'],
        padding: theme.spacing['spacing.general.x4'],
        gap: theme.spacing['spacing.general.x4'],
      }}
    >
      <Text
        style={{
          alignSelf: 'flex-start',
          color: theme.colors['surface.default'],
          fontWeight: '700',
          backgroundColor: theme.colors['text.brand'],
          borderRadius: theme.borderRadii['radius.sm'],
          paddingHorizontal: theme.spacing['spacing.general.x2'],
          paddingVertical: theme.spacing['spacing.general.x0,5'],
        }}
      >
        {t('itemInformationSection.new')}
      </Text>
      <Column gap="spacing.general.x4" justifyContent="space-between">
        <Column gap="spacing.general.x1">
          <Row alignItems="center" gap="spacing.general.x1">
            <MagicEdit height={20} width={20} color={theme.colors['text.primary']} />
            <Text variant="bodyBase" fontWeight="700" fontSize={16} color="text.primary">
              {t('itemInformationSection.aiGenerate.promptTitle')}
            </Text>
          </Row>
          <Text variant="bodyBase" color="text.placeholder">
            {t('itemInformationSection.aiGenerate.promptDescription')}
          </Text>
        </Column>
        <Row
          gap="spacing.general.x1"
          alignItems="center"
          justifyContent="center"
          alignSelf="center"
          style={{ width: '100%' }}
        >
          <Button onPress={onSkip} pressableType={PressableType.GestureHandler} variant="secondary" style={{ flex: 1 }}>
            <Text>{t('itemInformationSection.aiGenerate.skip')}</Text>
          </Button>
          <Button onPress={onPress} pressableType={PressableType.GestureHandler} variant="primary" style={{ flex: 1 }}>
            <Text color="surface.default">{t('itemInformationSection.aiGenerate.button')}</Text>
          </Button>
        </Row>
      </Column>
    </Column>
  );
}

function RevealOnMount({ children }: { children: React.ReactNode }) {
  return (
    <Animated.View
      entering={FadeInUp.duration(250)}
      exiting={FadeOutUp.duration(200)}
      layout={LinearTransition.duration(250)}
    >
      {children}
    </Animated.View>
  );
}

const aiStyles = StyleSheet.create({
  dot: {
    fontSize: 14,
    fontWeight: '700',
    color: theme.colors['text.brand'],
  },
});

interface AutomaticMessageSheetContentProps {
  assistantMessageDefault: string;
  onChangeAutomaticMessage: (text: string) => void;
  onListingCreated: () => void;
  onResetAutomaticMessage: () => void;
  setShowAssistantMessageBottomSheet: (value: boolean) => void;
  showAssistantMessageBottomSheet: boolean;
  t: (key: string) => string;
  watchAutomaticMessage?: string | null;
}

function ConciergerieBadge() {
  return (
    <Column
      alignItems="center"
      justifyContent="center"
      style={{
        backgroundColor: theme.colors['surface.tertiary'],
        borderRadius: theme.borderRadii['radius.full'],
        width: 24 * 0.9,
        height: 24 * 0.9,
      }}
    >
      <Signal height={18 * 0.9} width={18 * 0.9} color={theme.colors['text.brand']} />
    </Column>
  );
}

function ConciergerieOptOutBanner({
  enabled,
  onChangeEnabled,
  onPressHowItWorks,
  t,
}: {
  enabled: boolean;
  onChangeEnabled: (value: boolean) => void;
  onPressHowItWorks: () => void;
  t: ReturnType<typeof useTranslation<'new'>>['t'];
}) {
  return (
    <Column
      style={{
        borderRadius: theme.borderRadii['radius.lg'],
        backgroundColor: theme.colors['surface.primary-neutral'],
        padding: theme.spacing['spacing.general.x4'],
      }}
    >
      <Row alignItems="flex-start" gap="spacing.general.x4">
        <ConciergerieBadge />
        <Column gap="spacing.general.x1" style={{ flex: 1, paddingRight: theme.spacing['spacing.general.x1'] }}>
          <Text variant="bodyBase" fontSize={14} fontWeight="700" style={{ lineHeight: 17 }}>
            {t('itemInformationSection.assistant.optOutBanner.title')}
          </Text>
          <Text fontSize={12} color="text.placeholder" style={{ lineHeight: 14 }}>
            {t('itemInformationSection.assistant.optOutBanner.description')}
          </Text>
          <Pressable onPress={onPressHowItWorks}>
            <Text
              fontSize={14}
              style={{ color: theme.colors['text.brand'], fontWeight: '700', textDecorationLine: 'underline' }}
            >
              {t('itemInformationSection.assistant.knowMore.howItWorks')}
            </Text>
          </Pressable>
        </Column>
        <Switch value={enabled} onValueChange={onChangeEnabled} />
      </Row>
    </Column>
  );
}

// Bottom Sheet shown when clicking on the "how it works" button for explaination
export function AssistantBottomSheet({ visible, onClose }: { visible: boolean; onClose: () => void }) {
  const { t } = useTranslation('new');
  const [conciergerieBottomSheetsShown, setConciergerieBottomSheetsShown] = useStoredValue<boolean | null>(
    StorageKey.ConciergerieBottomSheetsShown,
    null,
  );
  return (
    <BottomSheet open={visible} onClose={onClose} onOpen={() => setConciergerieBottomSheetsShown(true)}>
      <Button
        onPress={onClose}
        pressableType={PressableType.GestureHandler}
        style={{ position: 'absolute', right: theme.spacing['spacing.general.x8'], top: 0, zIndex: 1 }}
        variant="plain"
      >
        <IconX strokeWidth={ICON_STROKE_WIDTH} />
      </Button>
      <Column
        gap="spacing.general.x4"
        alignItems="center"
        style={{ padding: 24, gap: theme.spacing['spacing.general.x0,5'] }}
      >
        <Text variant="headerXSmall" fontWeight="700">
          {t('itemInformationSection.assistant.knowMore.title')}
        </Text>
        <Column
          alignItems="center"
          justifyContent="center"
          style={{ backgroundColor: theme.colors['surface.tertiary'], borderRadius: 24, width: '100%', height: 180 }}
        >
          <Signal height={64} width={64} color={theme.colors['text.primary']} />
        </Column>
        {!conciergerieBottomSheetsShown ? (
          <Text
            style={{
              alignSelf: 'flex-start',
              color: theme.colors['surface.default'],
              fontWeight: '700',
              backgroundColor: theme.colors['text.brand'],
              borderRadius: theme.borderRadii['radius.sm'],
              paddingHorizontal: theme.spacing['spacing.general.x2'],
              paddingVertical: theme.spacing['spacing.general.x0,5'],
            }}
          >
            {t('itemInformationSection.new')}
          </Text>
        ) : null}
        <Text variant="bodyBase" style={{ textAlign: 'left' }}>
          <Trans
            i18nKey="itemInformationSection.assistant.knowMore.text1"
            t={t}
            components={{ strong: <Text variant="bodyBase" style={{ fontWeight: 'bold' }} /> }}
          />
        </Text>
        <Text variant="bodyBase" style={{ textAlign: 'left' }}>
          <Trans
            i18nKey="itemInformationSection.assistant.knowMore.text2"
            t={t}
            components={{ strong: <Text variant="bodyBase" style={{ fontWeight: 'bold' }} /> }}
          />
        </Text>
        <Text variant="bodyBase" style={{ textAlign: 'left' }}>
          <Trans
            i18nKey="itemInformationSection.assistant.knowMore.text3"
            t={t}
            components={{ strong: <Text variant="bodyBase" style={{ fontWeight: 'bold' }} /> }}
          />
        </Text>
      </Column>
    </BottomSheet>
  );
}

// Bottom Sheet shown after creating a request with the assistant for the first time
export function AssistantBottomSheet2({ visible, onClose }: { visible: boolean; onClose: () => void }) {
  const { t } = useTranslation('new');
  const [conciergerieBottomSheetsShown, setConciergerieBottomSheetsShown] = useStoredValue<boolean | null>(
    StorageKey.ConciergerieBottomSheetsShown,
    null,
  );

  const showBottomSheet2 = visible && !conciergerieBottomSheetsShown;
  return (
    <BottomSheet
      open={showBottomSheet2}
      onClose={() => {
        setConciergerieBottomSheetsShown(true);
        onClose();
      }}
    >
      <Button
        onPress={onClose}
        pressableType={PressableType.GestureHandler}
        style={{ position: 'absolute', right: theme.spacing['spacing.general.x8'], top: 0, zIndex: 1 }}
        variant="plain"
      >
        <IconX strokeWidth={ICON_STROKE_WIDTH} />
      </Button>
      <Column
        gap="spacing.general.x4"
        alignItems="center"
        style={{ padding: 24, gap: theme.spacing['spacing.general.x0,5'] }}
      >
        <Text variant="headerXSmall" fontWeight="700">
          {t('itemInformationSection.assistant.firstCreated.title')}
        </Text>
        <Column
          alignItems="center"
          justifyContent="center"
          style={{ backgroundColor: theme.colors['surface.tertiary'], borderRadius: 24, width: '100%', height: 180 }}
        >
          <AnimatedSignal height={64} width={64} color={theme.colors['text.primary']} />
        </Column>
        <Text variant="bodyBase" style={{ textAlign: 'left' }}>
          <Trans
            i18nKey="itemInformationSection.assistant.firstCreated.text1"
            t={t}
            components={{ strong: <Text variant="bodyBase" style={{ fontWeight: 'bold' }} /> }}
          />
        </Text>
        <Row
          style={{
            alignSelf: 'flex-start',
            gap: theme.spacing['spacing.general.x1'],
            borderRadius: theme.borderRadii['radius.sm'],
            backgroundColor: theme.colors['surface.tertiary-neutral'],
            padding: theme.spacing['spacing.general.x2'],
            width: '100%',
          }}
          alignItems="center"
        >
          <IconClock strokeWidth={ICON_STROKE_WIDTH} size={24} />
          <Text variant="bodyBase" style={{ textAlign: 'left' }}>
            <Trans
              i18nKey="itemInformationSection.assistant.firstCreated.text2"
              t={t}
              components={{ strong: <Text variant="bodyBase" style={{ fontWeight: 'bold' }} /> }}
            />
          </Text>
        </Row>
        <Text variant="bodyBase" style={{ textAlign: 'left' }}>
          <Trans
            i18nKey="itemInformationSection.assistant.firstCreated.text3"
            t={t}
            components={{ strong: <Text variant="bodyBase" style={{ fontWeight: 'bold' }} /> }}
          />
        </Text>
      </Column>
    </BottomSheet>
  );
}

export function AutomaticMessageSheetContent({
  assistantMessageDefault,
  onChangeAutomaticMessage,
  onListingCreated,
  onResetAutomaticMessage,
  setShowAssistantMessageBottomSheet,
  showAssistantMessageBottomSheet,
  t,
  watchAutomaticMessage,
}: AutomaticMessageSheetContentProps) {
  const { formState } = useFormContext<CreateListingDTO>();
  const automaticMessageError = formState.errors.automaticMessage;

  return (
    <Column paddingHorizontal="spacing.general.x5" paddingBottom="spacing.general.x4" gap="spacing.general.x5">
      <Row alignItems="center" justifyContent="center" gap="spacing.general.x2" alignSelf="center">
        <Text variant="bodyBase" fontSize={18} fontWeight="700" style={{ lineHeight: 22, textAlign: 'center' }}>
          {t('chooseAutomaticMessage.text1')}
        </Text>
      </Row>
      <Column gap="spacing.general.x2">
        <Text fontSize={14} fontWeight="700">
          {t('chooseAutomaticMessage.automaticMessageLabel')}
        </Text>
        <MultiLineTextInput
          assistantMessageDefault={assistantMessageDefault}
          onChangeText={onChangeAutomaticMessage}
          onReset={onResetAutomaticMessage}
          resetLabel={t('chooseAutomaticMessage.reset')}
          value={watchAutomaticMessage}
          inputStyle={{
            borderColor: automaticMessageError
              ? theme.colors['surface.error.default']
              : theme.colors['surface.disabled'],
          }}
        />
        {automaticMessageError ? (
          <FormError
            errorMessage={
              automaticMessageError.type === 'required'
                ? t('chooseAutomaticMessage.automaticMessageRequired')
                : automaticMessageError.message
            }
          />
        ) : null}
      </Column>
      <Text style={{ textAlign: 'center', color: '#888', fontSize: 14, marginBottom: 8 }}>
        {t('chooseAutomaticMessage.text2')}
      </Text>
      <Actions
        onListingCreated={onListingCreated}
        setShowAssistantMessageBottomSheet={setShowAssistantMessageBottomSheet}
        showAssistantMessageBottomSheet={showAssistantMessageBottomSheet}
      />
    </Column>
  );
}

export const useListingSchema = () => {
  // React Hook Form retains the resolver schema; keep it stable across form updates.
  return useMemo(
    () =>
      yup.object().shape(
        {
          addressIds: yup
            .array(yup.string().uuid().required())
            .required()
            .when('groupIds', {
              is: (groupIds?: CreateListingDTO['groupIds']) => groupIds?.length === 0,
              then: () => yup.array().min(1),
            }),
          categoryPrimaryId: yup.string().uuid().nullable().defined(),
          customDistance: yup
            .number()
            .nullable()
            .max(MAX_RADIUS_IN_METERS)
            .when(['type', 'subtype'], {
              is: (t: Listing['type'], s: Listing['subtype']) =>
                t === ListingType.request && s !== ListingSubtype.service,
              then: (s) => s.required(),
            }),
          customCenterLatitude: yup
            .number()
            .nullable()
            .when(['type', 'subtype'], {
              is: (t: Listing['type'], s: Listing['subtype']) =>
                t === ListingType.request && s !== ListingSubtype.service,
              then: (s) => s.required(),
            }),
          customCenterLongitude: yup
            .number()
            .nullable()
            .when(['type', 'subtype'], {
              is: (t: Listing['type'], s: Listing['subtype']) =>
                t === ListingType.request && s !== ListingSubtype.service,
              then: (s) => s.required(),
            }),
          description: yup
            .string()
            .max(2000)
            .when('type', {
              is: (t: Listing['type']) => t === ListingType.request,
              then: (s) => s.min(3).required(),
              otherwise: (s) =>
                s
                  .transform((v) => (v === '' ? null : v))
                  .nullable()
                  .defined(),
            }),
          end: yup
            .string()
            .nullable()
            .defined()
            .test('end-after-start', 'new.dateSection.endBeforeStart', (end, context) => {
              const { start } = context.parent as Pick<CreateListingDTO, 'start'>;
              return isListingAvailabilityRangeValid(start, end);
            }),
          groupIds: yup
            .array(yup.string().uuid().required())
            .required()
            .when('addressIds', {
              is: (addressIds?: CreateListingDTO['addressIds']) => addressIds?.length === 0,
              then: () => yup.array().min(1),
            }),
          item: yup.object({
            name: yup.string().min(3).max(64).nullable().required(),
          }),
          picture: yup.mixed<ImagePickerAsset>().nullable().defined(),
          pictures: yup.array().of(yup.mixed<ImagePickerAsset>().required()).optional(),
          pictureUrl: yup.string().nullable().defined(),
          start: yup.string().nullable().defined(),
          type: yup.mixed<Listing['type']>().required(),
          subtype: yup.mixed<Listing['subtype']>().required(),
          serviceCategoryId: yup.string().uuid().nullable().defined(),
          quantity: yup.number().min(1).nullable().defined(),
          estimatedPrice: yup.number().nullable().defined(),
          price: yup
            .string()
            .nullable()
            .defined()
            .when(['type', 'transactionMode'], {
              is: (type: ListingType, mode?: TransactionMode | null) =>
                type === ListingType.offer && mode === TransactionMode.sell,
              then: (s) => s.required(),
            }),
          inventory: yup.string().nullable().defined(),
          transactionMode: yup.mixed<TransactionMode>().nullable().defined(),
          urgent: yup
            .boolean()
            .defined()
            .test('urgent-requires-expirable', 'new.urgentSection.urgentRequiresExpirable', (urgent, context) => {
              const { subtype } = context.parent as Pick<CreateListingDTO, 'subtype'>;
              if (!urgent) return true;
              return subtype === ListingSubtype.expirable;
            }),
          blockedDates: yup.array().of(yup.string().required()).optional(),
          loanFee: yup.number().nullable().defined(),
          loanFeeDescription: yup.string().nullable().defined(),
          loanFeePaymentMethod: yup.string().nullable().defined(),
          timeStart: yup.string().nullable().defined(),
          timeEnd: yup.string().nullable().defined(),
          address: yup
            .object({
              administrativeArea: yup.string().nullable().defined(),
              city: yup.string().nullable().defined(),
              country: yup.string().nullable().defined(),
              postalCode: yup.string().required(),
              street: yup.string().nullable().defined(),
              unit: yup.string().nullable().defined(),
            })
            .nullable()
            .defined(),
          maxParticipants: yup.string().nullable().defined(),
          automaticMessage: yup.string().optional(),
          duplicates: yup.number().min(0).max(MAX_LISTING_DUPLICATES).integer().nullable().defined(),
        },
        [['groupIds', 'addressIds']],
      ) as yup.ObjectSchema<CreateListingDTO>,
    [],
  );
};

export interface NewScreenParams {
  defaultValues?: {
    addressIds?: string[];
    description?: string;
    end?: string;
    groupIds?: string[];
    item?: { name: string };
    start?: string;
    type?: ListingType;
    subtype?: ListingSubtype;
    serviceCategoryId?: string;
    pictureUrl?: string;
    picturesUrl?: string[];
    customDistance?: number;
    customCenterLatitude?: number;
    customCenterLongitude?: number;
    categoryPrimaryId?: string;
    quantity?: number;
    estimatedPrice?: number;
    price?: string;
    inventory?: string;
    urgent?: boolean;
    transactionMode?: TransactionMode;
    loanFee?: number;
    loanFeeDescription?: string;
    loanFeePaymentMethod?: string;
    timeStart?: string;
    timeEnd?: string;
    address?: Listing['address'];
    maxParticipants?: number;
    automaticMessage?: string;
    duplicates?: number;
  };
  newListingSource?: NewListingSource;
  newAddress?: Listing['address'];
  blockedDates?: string[];
  newServiceCategoryId?: string;
  newCategoryId?: string;
}

export interface ListingsVisibilityExclusionsPreference {
  addressIds?: string[];
  groupIds?: string[];
}

const NewContentComponent: ComponentType<
  Omit<ComponentProps<NavigationScreen<'new'>>, 'navigation'> & {
    navigation: SpecificNavigationProp<'new' | 'offer-hub'>;
    embedded?: boolean;
    onRequestFocus?: () => void;
  }
> = ({ navigation, route, embedded = false, onRequestFocus }) => {
  const { t } = useTranslation('new');

  const { data: user } = useUser();
  const showConciergerie = isFeatureEnabled(FeatureFlag.conciergerie, user?.featureFlags);

  // To accept / edit the automatic message
  const [showAssistantMessageBottomSheet, setShowAssistantMessageBottomSheet] = useState(false);
  // For the 'how it works' text
  const [assistantSheetVisible, setAssistantSheetVisible] = useState(false);
  const [conciergerieEnabled, setConciergerieEnabled] = useState(true);

  useNavigationOptions(
    navigation,
    {
      headerBackVisible: false,
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t('headerTitle')}
        </Text>
      ),
    },
    embedded,
  );

  const setPageTitle = usePageTitle('new');
  useEffect(() => {
    if (!embedded) setPageTitle('headerTitle');
  }, [embedded, setPageTitle]);

  useMyProfile();
  const { data: allAddresses } = useAllAddresses();
  const [listingsVisibilityExclusionsPreference, setListingsVisibilityExclusionsPreference] =
    useStoredValue<ListingsVisibilityExclusionsPreference>(StorageKey.ListingsVisibilityExclusionsPreference, {});
  const schema = useListingSchema();
  const form = useForm<CreateListingDTO>({
    defaultValues: getNewListingDefaults(route),
    schema,
  });
  const { discardNavigationAction, setDiscardNavigationAction, onListingCreated, discardChanges } =
    useNewListingNavigationGuard({ navigation, isDirty: form.formState.isDirty, onRequestFocus });

  const type = form.watch('type');
  const isRequest = type === ListingType.request;
  const isOffer = type === ListingType.offer;
  const useHauteGaspesieSectorVisibility = isWhiteLabel(WhiteLabel.HauteGaspesie);
  const useHauteGaspesieOfferForm = isOffer && useHauteGaspesieSectorVisibility;
  const { data: allGroups } = useListingVisibilityGroups(useHauteGaspesieSectorVisibility);

  const newListingSource = useAtomValue(newListingSourceAtom);
  const trackNewListingOpened = useEffectEvent(() => {
    track(Event.NewOpened, { source: route.params?.newListingSource ?? newListingSource });
  });
  useEffect(() => {
    trackNewListingOpened();
  }, []);

  // blockedDates are sent back from offer-availability screen, when saving new dates
  useEffect(() => {
    if (!route.params?.blockedDates) return;
    form.setValue('blockedDates', route.params.blockedDates, { shouldDirty: true });
  }, [form, route.params?.blockedDates]);

  const listingsVisibilityPreferenceLoaded = listingsVisibilityExclusionsPreference !== undefined;

  const watchSubtype = form.watch('subtype');
  const watchTransactionMode = form.watch('transactionMode');
  const isService = watchSubtype === ListingSubtype.service;
  const isExpirable = watchSubtype === ListingSubtype.expirable;

  // newServiceCategoryId is sent back from service-categories screen, when picking a new category
  useEffect(() => {
    if (!route.params?.newServiceCategoryId) return;
    form.setValue('serviceCategoryId', route.params.newServiceCategoryId, { shouldDirty: true });
    form.control.register('serviceCategoryId');
  }, [form, route.params?.newServiceCategoryId]);

  // newCategoryId is sent back from categories screen, when picking a new category
  useEffect(() => {
    if (!route.params?.newCategoryId) return;
    form.setValue('categoryPrimaryId', route.params.newCategoryId, { shouldDirty: true });
    form.control.register('categoryPrimaryId');
  }, [form, route.params?.newCategoryId]);

  // newAddress is sent back from my-address screen, when entering a new address
  useEffect(() => {
    if (!route.params?.newAddress) return;
    form.setValue('address', route.params.newAddress, { shouldDirty: true });
    form.control.register('categoryPrimaryId');
  }, [form, route.params?.newAddress]);

  const visibilityInitComplete = Boolean(allGroups && allAddresses && listingsVisibilityPreferenceLoaded);
  const { setValue: formSetValue } = form;
  const initializeVisibility = useEffectEvent(() => {
    if (!allGroups || !allAddresses || !listingsVisibilityExclusionsPreference) return;

    // If the user is in a municipal_closed group, default to only the group being selected
    const isMunicipalClosed = Boolean(allGroups.find((g) => g.subtype === 'municipal_closed'));
    const excludedAddresses =
      listingsVisibilityExclusionsPreference.addressIds ?? (isMunicipalClosed ? allAddresses.map((a) => a.id) : []);
    const excludedAddressIds = new Set(excludedAddresses);
    const excludedGroupIds = new Set(listingsVisibilityExclusionsPreference.groupIds);

    formSetValue(
      'addressIds',
      allAddresses.reduce<string[]>((addressIds, { id }) => {
        if (!excludedAddressIds.has(id)) {
          addressIds.push(id);
        }
        return addressIds;
      }, []),
    );
    formSetValue(
      'groupIds',
      allGroups.reduce<string[]>((groupIds, { id }) => {
        if (!excludedGroupIds.has(id)) {
          groupIds.push(id);
        }
        return groupIds;
      }, []),
    );
  });
  useEffect(() => {
    if (visibilityInitComplete) initializeVisibility();
  }, [visibilityInitComplete]);

  const {
    description,
    hasPictures,
    isAnalyzing,
    showAIBanner,
    setShowAIBanner,
    offerCreationStep,
    triggerImageAnalysis,
    skipOfferPictureStep,
    skipOfferGenerationStep,
  } = useOfferImageAnalysis({ form, isOffer, route });

  const isDonation = isDonationListing({ subtype: watchSubtype, transactionMode: watchTransactionMode });

  useEffect(() => {
    if (isOffer) {
      form.setValue('customDistance', null);
      form.setValue('customCenterLatitude', null);
      form.setValue('customCenterLongitude', null);
    }
  }, [form, isOffer]);

  const userPlan = useMyActivePlans().data?.[0];
  const showBaconBanner = isService && userPlan?.code.group?.id === '932d163e-eef8-438d-8708-6fe3eb0f9ed7'; // Beaconsfield

  const pagePadding = usePagePadding();

  const { assistantMessageDefault, onChangeAutomaticMessage, onResetAutomaticMessage, watchAutomaticMessage } =
    useAutomaticMessage({ createForm: form });

  const {
    shouldShowAccessory,
    shouldShowGeneralSection,
    shouldShowOfferPictureSkip,
    shouldShowOfferGeneratePrompt,
    shouldShowOfferAnalyzingBanner,
    shouldShowOfferDetails,
  } = getOfferCreationVisibility(
    isOffer,
    offerCreationStep,
    hasPictures,
    showAIBanner,
    isAnalyzing,
    useHauteGaspesieOfferForm,
  ); // We don't show the accessory during the 'image' step for offers to avoid overwhelming users with options before they've added a picture

  return (
    <FormProvider {...form}>
      <Page
        paddingStyle={embedded ? { ...pagePadding, paddingTop: 16 } : undefined}
        accessory={
          shouldShowAccessory ? (
            <Accessory
              style={{ paddingHorizontal: pagePadding.paddingLeft }} // For some reason, the paddingTop here doesn't work; used a Column below instead...
            >
              <Column style={{ height: theme.spacing['spacing.general.x5'] }} />
              <Row alignItems="center" justifyContent="space-between" gap="spacing.general.x4">
                <Column style={{ flex: 1 }}>
                  <Actions
                    conciergerieEnabled={conciergerieEnabled}
                    onListingCreated={onListingCreated}
                    setShowAssistantMessageBottomSheet={setShowAssistantMessageBottomSheet}
                    showAssistantMessageBottomSheet={showAssistantMessageBottomSheet}
                  />
                </Column>
              </Row>
            </Accessory>
          ) : null
        }
        extraScrollHeight={40}
        keyboardShouldPersistTaps="handled"
      >
        <NewListingFields
          useHauteGaspesieOfferForm={useHauteGaspesieOfferForm}
          form={form}
          visibilityInitComplete={visibilityInitComplete}
          setListingsVisibilityExclusionsPreference={setListingsVisibilityExclusionsPreference}
          showBaconBanner={showBaconBanner}
          isRequest={isRequest}
          useHauteGaspesieSectorVisibility={useHauteGaspesieSectorVisibility}
          shouldShowGeneralSection={shouldShowGeneralSection}
          isOffer={isOffer}
          setShowAIBanner={setShowAIBanner}
          offerCreationStep={offerCreationStep}
          isAnalyzing={isAnalyzing}
          route={route}
          showConciergerie={showConciergerie}
          conciergerieEnabled={conciergerieEnabled}
          setConciergerieEnabled={setConciergerieEnabled}
          setAssistantSheetVisible={setAssistantSheetVisible}
          t={t}
          shouldShowOfferPictureSkip={shouldShowOfferPictureSkip}
          skipOfferPictureStep={skipOfferPictureStep}
          shouldShowOfferGeneratePrompt={shouldShowOfferGeneratePrompt}
          triggerImageAnalysis={triggerImageAnalysis}
          skipOfferGenerationStep={skipOfferGenerationStep}
          shouldShowOfferAnalyzingBanner={shouldShowOfferAnalyzingBanner}
          shouldShowOfferDetails={shouldShowOfferDetails}
          hasPictures={hasPictures}
          description={description}
          isExpirable={isExpirable}
          isService={isService}
          isDonation={isDonation}
          allAddresses={allAddresses}
          allGroups={allGroups}
        />
      </Page>
      {/* Bottom sheet to allow the user to modify the automatic message */}
      {showConciergerie ? (
        <BottomSheet open={showAssistantMessageBottomSheet} onClose={() => setShowAssistantMessageBottomSheet(false)}>
          <AutomaticMessageSheetContent
            assistantMessageDefault={assistantMessageDefault}
            onChangeAutomaticMessage={onChangeAutomaticMessage}
            onListingCreated={onListingCreated}
            onResetAutomaticMessage={onResetAutomaticMessage}
            setShowAssistantMessageBottomSheet={setShowAssistantMessageBottomSheet}
            showAssistantMessageBottomSheet={showAssistantMessageBottomSheet}
            t={t}
            watchAutomaticMessage={watchAutomaticMessage}
          />
        </BottomSheet>
      ) : null}
      {showConciergerie ? (
        <AssistantBottomSheet visible={assistantSheetVisible} onClose={() => setAssistantSheetVisible(false)} />
      ) : null}
      <Modal onClose={() => setDiscardNavigationAction(null)} open={discardNavigationAction !== null} transparent>
        <Modal.Card>
          <Modal.Title allowClose onClose={() => setDiscardNavigationAction(null)}>
            {t('discardChanges.title')}
          </Modal.Title>
          <Text textAlign="center">{t('discardChanges.description')}</Text>
          <Column gap="spacing.general.x2">
            <Button onPress={() => setDiscardNavigationAction(null)} variant="primary">
              {t('discardChanges.keepEditing')}
            </Button>
            <Button onPress={discardChanges} variant="secondary">
              {t('discardChanges.leave')}
            </Button>
          </Column>
        </Modal.Card>
      </Modal>
    </FormProvider>
  );
};

export function NewContent(props: ComponentProps<typeof NewContentComponent>) {
  return (
    <ErrorBoundary>
      <NewContentComponent {...props} />
    </ErrorBoundary>
  );
}

export { NewContent as NewScreen };

function NewListingFields({
  useHauteGaspesieOfferForm,
  form,
  visibilityInitComplete,
  setListingsVisibilityExclusionsPreference,
  showBaconBanner,
  isRequest,
  useHauteGaspesieSectorVisibility,
  shouldShowGeneralSection,
  isOffer,
  setShowAIBanner,
  offerCreationStep,
  isAnalyzing,
  route,
  showConciergerie,
  conciergerieEnabled,
  setConciergerieEnabled,
  setAssistantSheetVisible,
  t,
  shouldShowOfferPictureSkip,
  skipOfferPictureStep,
  shouldShowOfferGeneratePrompt,
  triggerImageAnalysis,
  skipOfferGenerationStep,
  shouldShowOfferAnalyzingBanner,
  shouldShowOfferDetails,
  hasPictures,
  description,
  isExpirable,
  isService,
  isDonation,
  allAddresses,
  allGroups,
}: {
  useHauteGaspesieOfferForm: boolean;
  form: ReturnType<typeof useForm<CreateListingDTO>>;
  visibilityInitComplete: boolean;
  setListingsVisibilityExclusionsPreference: React.Dispatch<
    React.SetStateAction<ListingsVisibilityExclusionsPreference>
  >;
  showBaconBanner: boolean;
  isRequest: boolean;
  useHauteGaspesieSectorVisibility: boolean;
  shouldShowGeneralSection: boolean;
  isOffer: boolean;
  setShowAIBanner: React.Dispatch<React.SetStateAction<boolean>>;
  offerCreationStep: string;
  isAnalyzing: boolean;
  route: SpecificRouteProp<'new'>;
  showConciergerie: boolean;
  conciergerieEnabled: boolean;
  setConciergerieEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setAssistantSheetVisible: React.Dispatch<React.SetStateAction<boolean>>;
  t: ReturnType<typeof useTranslation<'new'>>['t'];
  shouldShowOfferPictureSkip: boolean;
  skipOfferPictureStep: () => void;
  shouldShowOfferGeneratePrompt: boolean;
  triggerImageAnalysis: () => void;
  skipOfferGenerationStep: () => void;
  shouldShowOfferAnalyzingBanner: boolean;
  shouldShowOfferDetails: boolean;
  hasPictures: boolean;
  description: string | null | undefined;
  isExpirable: boolean;
  isService: boolean;
  isDonation: boolean;
  allAddresses: ReturnType<typeof useAllAddresses>['data'];
  allGroups: ReturnType<typeof useListingVisibilityGroups>['data'];
}): React.ReactNode {
  if (useHauteGaspesieOfferForm) {
    return (
      <HauteGaspesieOfferForm
        form={form}
        setListingsVisibilityExclusionsPreference={
          visibilityInitComplete ? setListingsVisibilityExclusionsPreference : undefined
        }
      />
    );
  }
  return (
    <Column gap="spacing.general.x4">
      {showBaconBanner ? <BaconSection /> : undefined}

      {isRequest && useHauteGaspesieSectorVisibility ? <TransactionModeSection /> : null}

      {shouldShowGeneralSection ? (
        <GeneralSection
          onChange={(images) => {
            if (!isOffer) return;
            if (images.length === 0) {
              setShowAIBanner(false);
            } else if (offerCreationStep === 'image' || offerCreationStep === 'image-and-details') {
              setShowAIBanner(true);
            }
          }}
          isAnalyzing={isAnalyzing}
          newListingSource={route.params?.newListingSource}
        />
      ) : null}
      {showConciergerie && isRequest ? (
        <ConciergerieOptOutBanner
          enabled={conciergerieEnabled}
          onChangeEnabled={setConciergerieEnabled}
          onPressHowItWorks={() => setAssistantSheetVisible(true)}
          t={t}
        />
      ) : null}
      {shouldShowOfferPictureSkip ? (
        <Button
          alignSelf="center"
          onPress={skipOfferPictureStep}
          pressableType={PressableType.GestureHandler}
          variant="plain"
        >
          <Text color="text.primary" textDecorationLine="underline">
            {t('generalSection.addPictureLater')}
          </Text>
        </Button>
      ) : null}
      {shouldShowOfferGeneratePrompt ? (
        <RevealOnMount>
          <AiGeneratePromptBanner onPress={triggerImageAnalysis} onSkip={skipOfferGenerationStep} t={t} />
        </RevealOnMount>
      ) : null}
      {shouldShowOfferAnalyzingBanner ? (
        <RevealOnMount>
          <AiAnalyzingBanner t={t} />
        </RevealOnMount>
      ) : null}
      <NewListingDetails
        shouldShowOfferDetails={shouldShowOfferDetails}
        offerCreationStep={offerCreationStep}
        hasPictures={hasPictures}
        description={description}
        isOffer={isOffer}
        isExpirable={isExpirable}
        form={form}
        isRequest={isRequest}
        isService={isService}
        isDonation={isDonation}
        allAddresses={allAddresses}
        allGroups={allGroups}
        useHauteGaspesieSectorVisibility={useHauteGaspesieSectorVisibility}
        visibilityInitComplete={visibilityInitComplete}
        setListingsVisibilityExclusionsPreference={setListingsVisibilityExclusionsPreference}
      />
    </Column>
  );
}

function NewListingDetails({
  shouldShowOfferDetails,
  offerCreationStep,
  hasPictures,
  description,
  isOffer,
  isExpirable,
  form,
  isRequest,
  isService,
  isDonation,
  allAddresses,
  allGroups,
  useHauteGaspesieSectorVisibility,
  visibilityInitComplete,
  setListingsVisibilityExclusionsPreference,
}: {
  shouldShowOfferDetails: boolean;
  offerCreationStep: string;
  hasPictures: boolean;
  description: string | null | undefined;
  isOffer: boolean;
  isExpirable: boolean;
  form: ReturnType<typeof useForm<CreateListingDTO>>;
  isRequest: boolean;
  isService: boolean;
  isDonation: boolean;
  allAddresses: ReturnType<typeof useAllAddresses>['data'];
  allGroups: ReturnType<typeof useListingVisibilityGroups>['data'];
  useHauteGaspesieSectorVisibility: boolean;
  visibilityInitComplete: boolean;
  setListingsVisibilityExclusionsPreference: React.Dispatch<
    React.SetStateAction<ListingsVisibilityExclusionsPreference>
  >;
}): React.ReactNode {
  if (!shouldShowOfferDetails) return null;
  return (
    <RevealOnMount>
      <Column
        gap="spacing.general.x4"
        opacity={offerCreationStep === 'details' || offerCreationStep === 'image-and-details' ? 1 : 0.4}
        pointerEvents={offerCreationStep === 'details' || offerCreationStep === 'image-and-details' ? 'auto' : 'none'}
      >
        <NewListingBonification
          hasPictures={hasPictures}
          description={description}
          isOffer={isOffer}
          offerCreationStep={offerCreationStep}
        />

        <ItemInformationSection />

        {isFeatureEnabled(FeatureFlag.listingPromptAvailability) && isOffer && !isExpirable ? (
          <AvailabilitySection form={form} />
        ) : null}
        {isRequest && !isService ? <TravelDistanceSection form={form} /> : undefined}
        {isRequest && !isDonation ? <DateSection /> : undefined}
        {isRequest && isService ? <PublicServiceRequestSection /> : undefined}
        <NewListingVisibility
          allAddresses={allAddresses}
          allGroups={allGroups}
          useHauteGaspesieSectorVisibility={useHauteGaspesieSectorVisibility}
          visibilityInitComplete={visibilityInitComplete}
          setListingsVisibilityExclusionsPreference={setListingsVisibilityExclusionsPreference}
        />
      </Column>
    </RevealOnMount>
  );
}

function NewListingVisibility({
  allAddresses,
  allGroups,
  useHauteGaspesieSectorVisibility,
  visibilityInitComplete,
  setListingsVisibilityExclusionsPreference,
}: {
  allAddresses: ReturnType<typeof useAllAddresses>['data'];
  allGroups: ReturnType<typeof useListingVisibilityGroups>['data'];
  useHauteGaspesieSectorVisibility: boolean;
  visibilityInitComplete: boolean;
  setListingsVisibilityExclusionsPreference: React.Dispatch<
    React.SetStateAction<ListingsVisibilityExclusionsPreference>
  >;
}): React.ReactNode {
  return (allAddresses && allAddresses.length > 1) || allGroups?.length ? (
    <Column marginTop="spacing.general.x4">
      <GroupsSection
        includeAllSectorGroups={useHauteGaspesieSectorVisibility}
        setListingsVisibilityExclusionsPreference={
          visibilityInitComplete ? setListingsVisibilityExclusionsPreference : undefined
        }
      />
    </Column>
  ) : undefined;
}

function NewListingBonification({
  hasPictures,
  description,
  isOffer,
  offerCreationStep,
}: {
  hasPictures: boolean;
  description: string | null | undefined;
  isOffer: boolean;
  offerCreationStep: string;
}): React.ReactNode {
  return (!hasPictures || !description) &&
    isOffer &&
    (offerCreationStep === 'details' || offerCreationStep === 'image-and-details') ? (
    <RevealOnMount>
      <Column
        gap="spacing.general.x3"
        paddingLeft="spacing.general.x4"
        paddingVertical="spacing.general.x3"
        paddingRight="spacing.general.x1"
        style={{
          shadowColor: theme.colors['text.primary'],
          shadowOffset: { width: 0, height: 0 },
          shadowOpacity: 0.1,
          shadowRadius: 1,
          backgroundColor: theme.colors['surface.warning.subtle'],
          borderRadius: theme.borderRadii['radius.lg'],
        }}
      >
        <Row>
          <BonificationBadge />
        </Row>
        <Text variant="bodyBase">
          <Trans components={{ strong: <Text fontWeight="700" /> }} i18nKey="bonificationPrompt" ns="new" />
        </Text>
      </Column>
    </RevealOnMount>
  ) : null;
}

function getOfferCreationVisibility(
  isOffer: boolean,
  offerCreationStep: string,
  hasPictures: boolean,
  showAIBanner: boolean,
  isAnalyzing: boolean,
  useHauteGaspesieOfferForm: boolean,
) {
  const shouldShowOfferDetails =
    !isOffer ||
    offerCreationStep === 'details' ||
    offerCreationStep === 'image-and-details' ||
    offerCreationStep === 'generate'; // We show the details section during the 'generate' step for offers if we're analyzing to avoid showing an empty screen, but we disable it (opacity 0.5) to signal that it's not actionable until the analysis is done
  const shouldShowGeneralSection =
    !isOffer ||
    offerCreationStep === 'image' ||
    offerCreationStep === 'image-and-details' ||
    offerCreationStep === 'generate' ||
    hasPictures; // GeneralSection includes the picture upload, so we hide it during the 'image' step for offers
  const shouldShowOfferPictureSkip = isOffer && offerCreationStep === 'image';
  const shouldShowOfferGeneratePrompt =
    isOffer && (offerCreationStep === 'generate' || showAIBanner) && hasPictures && !isAnalyzing;
  const shouldShowOfferAnalyzingBanner = isOffer && offerCreationStep === 'generate' && isAnalyzing;
  const shouldShowAccessory =
    useHauteGaspesieOfferForm || !isOffer || (offerCreationStep !== 'image' && offerCreationStep !== 'generate'); // We don't show the accessory during the 'image' step for offers to avoid overwhelming users with options before they've added a picture
  return {
    shouldShowAccessory,
    shouldShowGeneralSection,
    shouldShowOfferPictureSkip,
    shouldShowOfferGeneratePrompt,
    shouldShowOfferAnalyzingBanner,
    shouldShowOfferDetails,
  };
}

function getNewListingDefaults(
  route: SpecificRouteProp<'new'>,
): import('react-hook-form').DefaultValues<CreateListingDTO> {
  return {
    addressIds: [],
    description: route.params?.defaultValues?.description ?? '',
    end: route.params?.defaultValues?.end ?? null,
    groupIds: route.params?.defaultValues?.groupIds ?? [],
    item: route.params?.defaultValues?.item ?? {},
    start: route.params?.defaultValues?.start ?? null,
    type: route.params?.defaultValues?.type ?? ListingType.offer,
    subtype: route.params?.defaultValues?.subtype ?? ListingSubtype.object,
    serviceCategoryId: route.params?.defaultValues?.serviceCategoryId ?? null,
    picture: null,
    pictures: [],
    pictureUrl: route.params?.defaultValues?.pictureUrl ?? null,
    customDistance: route.params?.defaultValues?.customDistance ?? null,
    customCenterLatitude: route.params?.defaultValues?.customCenterLatitude ?? null,
    customCenterLongitude: route.params?.defaultValues?.customCenterLongitude ?? null,
    categoryPrimaryId: route.params?.defaultValues?.categoryPrimaryId ?? null,
    quantity: route.params?.defaultValues?.quantity ?? null,
    estimatedPrice: route.params?.defaultValues?.estimatedPrice ?? null,
    price: route.params?.defaultValues?.price ?? null,
    inventory: route.params?.defaultValues?.inventory ?? null,
    urgent: route.params?.defaultValues?.urgent ?? false,
    transactionMode: route.params?.defaultValues?.transactionMode ?? null,
    loanFee: route.params?.defaultValues?.loanFee ?? null,
    loanFeeDescription: route.params?.defaultValues?.loanFeeDescription ?? null,
    loanFeePaymentMethod: route.params?.defaultValues?.loanFeePaymentMethod ?? null,
    timeStart: route.params?.defaultValues?.timeStart ?? null,
    timeEnd: route.params?.defaultValues?.timeEnd ?? null,
    address: route.params?.defaultValues?.address ?? null,
    maxParticipants: route.params?.defaultValues?.maxParticipants?.toFixed(0) ?? null,
    automaticMessage: '',
    duplicates: route.params?.defaultValues?.duplicates ?? null,
  };
}

function useOfferImageAnalysis({
  form,
  isOffer,
  route,
}: {
  form: ReturnType<typeof useForm<CreateListingDTO>>;
  isOffer: boolean;
  route: SpecificRouteProp<'new'>;
}) {
  const { t } = useTranslation('new');
  const [offerCreationStep, setOfferCreationStep] = useState<OfferCreationStep>(() =>
    getInitialOfferCreationStep(route.params?.defaultValues),
  );
  const description = form.watch('description');
  const title = form.watch('item.name');
  const pictures = form.watch('pictures');
  const hasPictures = (pictures?.length ?? 0) > 0;
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAIBanner, setShowAIBanner] = useState(false);

  const firstPicture = pictures?.[0];
  const { mutate: analyzeImage } = useAnalyzeListingImage();

  const synchronizeOfferCreationStep = useEffectEvent(() => {
    if (!isOffer) {
      setOfferCreationStep('details');
      return;
    }

    if (offerCreationStep === 'image' && hasPictures) {
      setOfferCreationStep('generate');
      return;
    }

    if (
      (offerCreationStep === 'generate' ||
        offerCreationStep === 'details' ||
        offerCreationStep === 'image-and-details') &&
      !hasPictures &&
      !isAnalyzing
    ) {
      if (offerCreationStep === 'generate') {
        setOfferCreationStep('image');
      } else if (offerCreationStep === 'image-and-details' || description || title) {
        setOfferCreationStep('image-and-details');
      } else {
        setOfferCreationStep('image');
      }
    }
  });
  useEffect(() => {
    const timeout = setTimeout(synchronizeOfferCreationStep, 0);
    return () => clearTimeout(timeout);
  }, [description, hasPictures, isAnalyzing, isOffer, offerCreationStep, title]);

  const triggerImageAnalysis = () => {
    if (isAnalyzing || !firstPicture) return;

    const pictureAtCall = firstPicture;
    setOfferCreationStep('generate');
    setIsAnalyzing(true);

    analyzeImage(pictureAtCall, {
      onSuccess: (result: { title: string; description: string }) => {
        setIsAnalyzing(false);

        const currentFirstPicture = form.getValues('pictures')?.[0];
        if (!arePicturesEquivalent(currentFirstPicture, pictureAtCall)) return;

        form.setValue('item.name', result.title, { shouldDirty: true, shouldValidate: true });
        form.setValue('description', result.description, { shouldDirty: true, shouldValidate: true });
        setShowAIBanner(false);
        setOfferCreationStep('details');
      },
      onError: (error: Error) => {
        setIsAnalyzing(false);

        const currentFirstPicture = form.getValues('pictures')?.[0];
        if (!arePicturesEquivalent(currentFirstPicture, pictureAtCall)) return;

        const isRateLimit = error instanceof AxiosError && error.response?.status === 429;
        Toast.show({
          type: 'error',
          text1: isRateLimit
            ? t('itemInformationSection.aiGenerate.rateLimitError')
            : t('itemInformationSection.aiGenerate.error'),
        });
        setOfferCreationStep('details');
      },
    });
  };

  const skipOfferPictureStep = () => setOfferCreationStep('image-and-details');
  const skipOfferGenerationStep = () => {
    setOfferCreationStep('details');
    setShowAIBanner(false);
  };

  return {
    description,
    hasPictures,
    isAnalyzing,
    showAIBanner,
    setShowAIBanner,
    offerCreationStep,
    triggerImageAnalysis,
    skipOfferPictureStep,
    skipOfferGenerationStep,
  };
}

function useNewListingNavigationGuard({
  navigation,
  isDirty,
  onRequestFocus,
}: {
  navigation: SpecificNavigationProp<'new' | 'offer-hub'>;
  isDirty: boolean;
  onRequestFocus?: () => void;
}) {
  const [discardNavigationAction, setDiscardNavigationAction] = useState<NavigationAction | null>(null);
  const allowNavigationRef = useRef(false);
  const resetNewListingSource = useResetAtom(newListingSourceAtom);

  useEffect(
    () =>
      navigation.addListener('beforeRemove', (event) => {
        if (!isDirty || allowNavigationRef.current) {
          resetNewListingSource();
          return;
        }

        event.preventDefault();
        onRequestFocus?.();
        setDiscardNavigationAction(event.data.action);
      }),
    [isDirty, navigation, onRequestFocus, resetNewListingSource],
  );

  const onListingCreated = () => {
    allowNavigationRef.current = true;
  };

  const discardChanges = () => {
    if (!discardNavigationAction) return;

    allowNavigationRef.current = true;
    const action = discardNavigationAction;
    setDiscardNavigationAction(null);
    resetNewListingSource();
    navigation.dispatch(action);
  };

  return { discardNavigationAction, setDiscardNavigationAction, onListingCreated, discardChanges };
}
